import { Flex, Popconfirm, Tag, Tooltip } from 'antd';
import { AuthorizedIcon, Icon } from '../../../components';
import TrashSolidBlack from '../../../assets/icons/trash-solid-b-black.svg';
import PenToSquareSolidBlack from '../../../assets/icons/pen-to-square-solid-w-black.svg';
import EyeRegularWBlack from '../../../assets/icons/eye-regular-w-black.svg';
import Variant99Black from '../../../assets/icons/variant99-black.svg';
import FolderOpenRegular from '../../../assets/icons/folder-open-regular.svg';
import styles from './index.module.scss';
import { status_colors } from '../../../assets/conts';
import { IEnum } from '../../../services/type';
import { getValueFromEnum } from '../../../utils/functions';
import { ColumnType } from 'antd/es/table';
import { actionTypes } from '../../../assets/enums';
import {
  IFile,
  IOrganization,
  IExpeditionCertificateDataSource,
} from '../../../types';

export const getColumns = (
  handleClickEye: (id: number) => void,
  handleOpenEditModal: (id: number) => void,
  handleClickDelete: (id: number) => Promise<void>,
  handleClickCancel: (id: number) => Promise<void>,
  handleSetAdditionalFile: (id: number, file: IFile[]) => void,
  organizations: IOrganization[],
  currencies: IEnum[],
  statusChoices: IEnum[],
  transportationTypes: IEnum[],
): Array<
  ColumnType<IExpeditionCertificateDataSource> & { searchable?: boolean }
> => [
  {
    title: 'Şirket',
    dataIndex: 'organization',
    key: 'organization',
    width: 125,
    searchable: true,
    filters: organizations.map((item) => ({
      text: item.name || '',
      value: item.id,
    })),
    sorter: (a, b) => a.organization - b.organization,
    render: (_, record) => (
      <Tooltip
        title={
          organizations.find((item) => item.id === record.organization)?.name
        }
      >
        <span>{`${organizations.find((item) => item.id === record.organization)?.name.slice(0, 15)}...`}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Poliçe No',
    dataIndex: 'policy_number',
    key: 'policy_number',
    searchable: true,
    width: 150,
    sorter: (a, b) => a.policy_number?.localeCompare(b.policy_number),
  },
  {
    title: 'Sevk Tipi',
    dataIndex: 'transportation_type',
    key: 'transportation_type',
    width: 125,
    filters: transportationTypes.map((item) => ({
      text: getValueFromEnum(transportationTypes, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) =>
      a.transportation_type?.localeCompare(b.transportation_type),
    render: (_: unknown, record: IExpeditionCertificateDataSource) => (
      <Tag className={styles[`${record.transportation_type}Tag`]}>
        {getValueFromEnum(transportationTypes, record.transportation_type)}
      </Tag>
    ),
  },
  {
    title: 'Sevk Tarihi',
    dataIndex: 'expedition_date',
    key: 'expedition_date',
    width: 125,
    sorter: (a, b) => a.expedition_date?.localeCompare(b.expedition_date),
  },
  {
    title: 'Emtia Bedeli',
    dataIndex: 'insurance_value',
    key: 'insurance_value',
    width: 130,
    sorter: (a, b) => (a.insurance_value || 0) - (b.insurance_value || 0),
    render: (_: unknown, record: IExpeditionCertificateDataSource) => (
      <div>
        {record.insurance_value
          ? `${record.insurance_value} ${getValueFromEnum(currencies, record.insurance_value_unit)}`
          : ''}
      </div>
    ),
  },
  {
    title: 'Poliçe Primi',
    dataIndex: 'policy_value',
    key: 'policy_value',
    width: 125,
    sorter: (a, b) => (a.policy_value || 0) - (b.policy_value || 0),
    render: (_: unknown, record: IExpeditionCertificateDataSource) => (
      <div>
        {record.policy_value
          ? `${record.policy_value} ${getValueFromEnum(currencies, record.policy_value_unit)}`
          : ''}
      </div>
    ),
  },
  {
    title: 'Sertifika Durumu',
    dataIndex: 'recent_status',
    key: 'recent_status',
    width: 175,
    filters: statusChoices.map((item) => ({
      text: getValueFromEnum(statusChoices, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    render: (_: unknown, record: IExpeditionCertificateDataSource) => (
      <Tag color={status_colors[record.recent_status]}>
        {getValueFromEnum(statusChoices, record.recent_status)}
      </Tag>
    ),
  },
  {
    title: 'Açıklama',
    dataIndex: 'description',
    key: 'description',
    width: 125,
    sorter: (a, b) => a.description?.localeCompare(b.description),
    render: (_: unknown, record: IExpeditionCertificateDataSource) => (
      <Tooltip title={record.description}>
        <span>{`${record.description?.slice(0, 20)}...`}</span>
      </Tooltip>
    ),
  },
  {
    title: 'İşlem',
    dataIndex: 'action',
    key: 'action',
    width: 160,
    render: (_: unknown, record: IExpeditionCertificateDataSource) => (
      <Flex justify="flex-end" gap={14}>
        <Icon
          popoverText={actionTypes.VIEW}
          source={EyeRegularWBlack}
          onClick={() => record?.id && handleClickEye(record.id)}
        />
        {record.recent_status !== 'cancelled' && (
          <Popconfirm
            title={
              <div className={styles.popconfirmTitle}>
                İptal işlemini onaylıyor musunuz?
              </div>
            }
            overlayInnerStyle={{
              width: 150,
              borderColor: '#D6222A',
              borderWidth: 1,
              borderStyle: 'solid',
            }}
            okText="Onayla"
            cancelText="İptal"
            icon={null}
            color="#FAFAFA"
            okButtonProps={{ danger: true, style: { width: '45%' } }}
            cancelButtonProps={{ danger: true, style: { width: '45%' } }}
            onConfirm={() => record.id && handleClickCancel(record.id)}
          >
            <AuthorizedIcon
              action="add"
              model="expeditioncertificate"
              popoverText={actionTypes.CANCEL}
              source={Variant99Black}
            />
          </Popconfirm>
        )}
        <Popconfirm
          title={
            <div className={styles.popconfirmTitle}>
              Silme işlemini onaylıyor musunuz?
            </div>
          }
          overlayInnerStyle={{
            width: 150,
            borderColor: '#D6222A',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
          okText="Onayla"
          cancelText="İptal"
          icon={null}
          color="#FAFAFA"
          okButtonProps={{ danger: true, style: { width: '45%' } }}
          cancelButtonProps={{ danger: true, style: { width: '45%' } }}
          onConfirm={() => record.id && handleClickDelete(record.id)}
        >
          <AuthorizedIcon
            action="delete"
            model="expeditioncertificate"
            popoverText={actionTypes.DELETE}
            source={TrashSolidBlack}
          />
        </Popconfirm>
        <AuthorizedIcon
          action="change"
          model="expeditioncertificate"
          popoverText={actionTypes.CHANGE}
          source={PenToSquareSolidBlack}
          onClick={() => record?.id && handleOpenEditModal(record.id)}
        />
        <AuthorizedIcon
          action="view"
          model="expeditioncertificateadditionalfile"
          popoverText={actionTypes.ADDITIONAL_FILE}
          source={FolderOpenRegular}
          onClick={() =>
            handleSetAdditionalFile(record?.id || 0, record.additional_files)
          }
        />
      </Flex>
    ),
  },
];
